import React from 'react';
import PropTypes from 'prop-types';
import { Error, Warning, CheckCircle, CreditCard, Replay } from '@mui/icons-material';
import {
  Card,
  AmountContainer,
  AmountGrid,
  AmountIcon,
  AmountContent,
  AmountLabel,
  AmountValue,
} from './styles';
import CustomButton from 'components/CustomButton';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';

const Props = {
  invoice: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  alert: PropTypes.node,
};

const download = (
  <svg width="34" height="36" viewBox="0 0 34 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="33" height="35" rx="1.5" fill="#F5F5F5" stroke="#E0E0E0" />
    <path
      d="M24 15.7059H20V9H14V15.7059H10L17 23.5294L24 15.7059ZM10 25.7647V28H24V25.7647H10Z"
      fill="#616161"
    />
  </svg>
);

const paidStatus = <CheckCircle fontSize="large" style={{ color: '#55a30b' }} />;

const errorStatus = <Error fontSize="large" style={{ color: '#F01C10' }} />;

const openStatus = <Warning fontSize="large" style={{ color: '#ff9226' }} />;

const getStatusIcon = (status) => {
  return {
    paid: paidStatus,
    error: errorStatus,
    open: openStatus,
  }[status];
};

const getActionLabel = (status) => {
  return {
    paid: 'Receipt',
    error: 'Retry Payment',
    open: 'Add Card',
  }[status];
};

const getActionIcon = (status) => {
  return {
    paid: download,
    error: <Replay fontSize="large" style={{ color: '#616161' }} />,
    open: <CreditCard fontSize="large" style={{ color: '#616161' }} />,
  }[status];
};

export default function InvoiceBox({ invoice, onClick, status, alert, user }) {
  const router = useRouter();
  const activeFapiaoDate = new Date('2023-12-14T00:00:00').getTime();
  const dateFapiao = new Date(invoice.created).getTime() > activeFapiaoDate;
  const showFapiaoButton = invoice.gateway === 'AliPay' && dateFapiao && user.country_code === 'CN';
  const requestedFapiao = invoice.requestedFapiao === true;

  return (
    <Card>
      <AmountContainer>
        <AmountGrid>
          <AmountLabel>Date</AmountLabel>
          <AmountValue>{invoice.formattedDate}</AmountValue>
        </AmountGrid>

        {user.country_code === 'CN' && (
          <AmountGrid>
            <AmountLabel>ID</AmountLabel>
            <AmountValue>{invoice.id}</AmountValue>
          </AmountGrid>
        )}

        <AmountGrid>
          <AmountLabel>Plan</AmountLabel>
          <AmountValue>
            {invoice.formattedPlan === 'undefined undefined' ? 'Upsell' : invoice.formattedPlan}
          </AmountValue>
        </AmountGrid>

        <AmountGrid>
          <AmountLabel>Amount</AmountLabel>
          <AmountValue>{`${invoice.currency} ${
            invoice.formattedTotal ?? invoice.total
          }`}</AmountValue>
        </AmountGrid>

        <AmountGrid>
          <AmountContent>
            <AmountLabel noMargin>Status</AmountLabel>
            {getStatusIcon(status)}
          </AmountContent>
        </AmountGrid>

        {onClick && (
          <Box pr={user.country_code === 'CN' && 19.26}>
            <AmountGrid>
              <AmountContent>
                <AmountLabel noMargin>{getActionLabel(status)}</AmountLabel>
                <AmountIcon onClick={onClick}>{getActionIcon(status)}</AmountIcon>
              </AmountContent>
            </AmountGrid>
          </Box>
        )}

        {showFapiaoButton && (
          <Box ml={-20.26}>
            <CustomButton
              style={{ marginLeft: requestedFapiao && 14 }}
              disabled={requestedFapiao}
              onClick={() => router.push(`/my-account/billing/fapiao?inv=${invoice.id}`)}
            >
              {requestedFapiao ? 'Requested' : 'Apply Fapiao'}
            </CustomButton>
          </Box>
        )}
      </AmountContainer>
      {alert && alert}
    </Card>
  );
}

InvoiceBox.propTypes = Props;
